(() => {
  'use strict';

  if (window.__foodnetMotionV114) return;
  window.__foodnetMotionV114 = true;

  const motionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const reduced = () => Boolean(motionQuery?.matches);
  const EASE_OUT = 'cubic-bezier(.16,1,.3,1)';
  const EASE_STANDARD = 'cubic-bezier(.2,.7,.2,1)';
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  const animate = (element, keyframes, options) => {
    if (!element || reduced() || typeof element.animate !== 'function') return null;
    try {
      element.getAnimations?.().forEach((animation) => {
        if (animation.id?.startsWith('fn114-')) animation.cancel();
      });
      const animation = element.animate(keyframes, options);
      animation.id = `fn114-${Math.random().toString(36).slice(2)}`;
      return animation;
    } catch (_) {
      return null;
    }
  };

  let leaving = false;
  const resetPageMotion = () => {
    leaving = false;
    document.body?.classList.remove('fn-motion-page-leave');
  };

  window.addEventListener('pageshow', resetPageMotion);

  document.addEventListener('click', (event) => {
    if (reduced() || leaving || event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target.closest?.('a[href]');
    if (!link || link.hasAttribute('download') || link.target === '_blank') return;
    if (link.matches('[data-no-page-motion], [data-open-drawer], [data-wishlist]')) return;
    if (link.closest('[data-no-page-motion]')) return;

    const raw = link.getAttribute('href') || '';
    if (!raw || raw.startsWith('#') || /^(mailto:|tel:|javascript:)/i.test(raw)) return;

    let url;
    try { url = new URL(link.href, window.location.href); } catch (_) { return; }
    if (url.origin !== window.location.origin) return;
    if (/\/checkout(?:\/|$)/.test(url.pathname)) return;
    if (url.pathname === location.pathname && url.search === location.search && url.hash) return;

    event.preventDefault();
    leaving = true;
    document.body?.classList.add('fn-motion-page-leave');
    window.setTimeout(() => window.location.assign(url.href), 155);
  });

  const revealSelector = [
    '.product-card',
    '.wk73-line',
    '.w19-drawer-item',
    '.wk-mobile-menu__item',
    '.wk-mobile-menu__service-row',
    '.w88-panel__grid > *',
    '.predictive-card'
  ].join(',');

  let revealObserver = null;
  const revealNow = (element) => {
    element.classList.add('is-fn-motion-visible');
    revealObserver?.unobserve(element);
  };

  const prepareReveal = (root = document) => {
    $$(revealSelector, root).forEach((element, index) => {
      if (element.dataset.fnMotionReveal === '1') return;
      if (element.classList.contains('w66-reveal') || element.classList.contains('w68-reveal')) return;
      element.dataset.fnMotionReveal = '1';
      element.classList.add('fn-motion-reveal');
      element.style.setProperty('--fn-motion-delay', `${Math.min(index % 6, 5) * 34}ms`);
      if (reduced() || !revealObserver) revealNow(element);
      else revealObserver.observe(element);
    });
  };

  if (!reduced() && 'IntersectionObserver' in window) {
    revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) revealNow(entry.target);
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -4% 0px' });
  }

  const closingTargets = new WeakMap();
  const closeConfigs = [
    { selector: '[data-cart-drawer]', kind: 'drawer', duration: 220 },
    { selector: '[data-search-drawer]', kind: 'drawer', duration: 220 },
    { selector: '[data-mobile-menu]', kind: 'legacy-menu', duration: 220 },
    { selector: '[data-wk-mobile-menu]', kind: 'modern-menu', duration: 250 },
    { selector: '[data-w88-mega]', kind: 'mega', duration: 180 }
  ];

  const semanticOpen = (element, kind) => {
    if (element.getAttribute('aria-hidden') === 'false' || element.classList.contains('is-open')) return true;
    if (kind === 'modern-menu' && document.body?.classList.contains('wk-mobile-menu-open')) return true;
    if (kind === 'mega') {
      const id = element.id;
      if (id) {
        const trigger = document.querySelector(`[aria-controls="${CSS.escape(id)}"][aria-expanded="true"]`);
        if (trigger) return true;
      }
    }
    return false;
  };

  const anotherDrawerIsOpening = (element) => {
    const open = document.querySelector('[data-cart-drawer].is-open,[data-search-drawer].is-open,[data-mobile-menu].is-open');
    return Boolean(open && open !== element);
  };

  const animateOpenSurface = (element, kind) => {
    if (reduced()) return;
    if (kind === 'modern-menu') {
      const items = $$('.wk-mobile-menu__item:not([hidden]), .wk-mobile-menu__service-row:not([hidden])', element).slice(0, 10);
      items.forEach((item, index) => animate(item, [
        { opacity: 0, transform: 'translate3d(-8px,10px,0)' },
        { opacity: 1, transform: 'translate3d(0,0,0)' }
      ], { duration: 300, delay: 65 + index * 28, easing: EASE_OUT, fill: 'both' }));
      return;
    }
    if (kind === 'mega') {
      const activePanel = $('[data-w88-panel]:not([hidden])', element);
      animate(activePanel, [
        { opacity: 0, transform: 'translate3d(0,-7px,0)' },
        { opacity: 1, transform: 'translate3d(0,0,0)' }
      ], { duration: 260, easing: EASE_OUT, fill: 'both' });
      return;
    }
    const primary = kind === 'drawer'
      ? element.querySelector('.cart-drawer__body,.search-drawer__body,[class*="drawer__body"]')
      : element.firstElementChild;
    animate(primary, [
      { opacity: 0.72, transform: 'translate3d(8px,0,0)' },
      { opacity: 1, transform: 'translate3d(0,0,0)' }
    ], { duration: 260, easing: EASE_OUT, fill: 'both' });
  };

  const setupClosingTarget = (element, kind, duration) => {
    if (!element || closingTargets.has(element)) return;
    let seenOpen = !element.hidden;
    let closing = false;
    let committing = false;
    let timer = 0;

    const stopClosing = () => {
      window.clearTimeout(timer);
      timer = 0;
      closing = false;
      element.classList.remove('fn-motion-closing');
    };

    const observer = new MutationObserver(() => {
      if (committing) {
        committing = false;
        return;
      }

      if (!element.hidden) {
        if (closing) {
          if (semanticOpen(element, kind)) {
            stopClosing();
            seenOpen = true;
            animateOpenSurface(element, kind);
          }
          return;
        }
        if (!seenOpen) animateOpenSurface(element, kind);
        seenOpen = true;
        return;
      }

      if (!seenOpen || closing || reduced() || document.visibilityState === 'hidden') {
        seenOpen = false;
        return;
      }
      if (kind === 'drawer' && anotherDrawerIsOpening(element)) {
        seenOpen = false;
        return;
      }

      closing = true;
      element.hidden = false;
      element.classList.add('fn-motion-closing');

      timer = window.setTimeout(() => {
        if (semanticOpen(element, kind)) {
          stopClosing();
          seenOpen = true;
          return;
        }
        committing = true;
        element.hidden = true;
        element.classList.remove('fn-motion-closing');
        closing = false;
        seenOpen = false;
      }, duration);
    });

    observer.observe(element, { attributes: true, attributeFilter: ['hidden', 'class', 'aria-hidden'] });
    closingTargets.set(element, observer);
  };

  const bindClosingTargets = (root = document) => {
    closeConfigs.forEach(({ selector, kind, duration }) => {
      $$(selector, root).forEach((element) => setupClosingTarget(element, kind, duration));
    });
  };

  const heroObservers = new WeakMap();
  const bindHero = (hero) => {
    if (!hero || heroObservers.has(hero)) return;
    const slides = $$('[data-hero-slide]', hero);
    if (slides.length < 2) return;
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        const slide = mutation.target;
        if (slide.hidden) return;
        animate(slide, [
          { opacity: 0.15, transform: 'translate3d(18px,0,0) scale(.995)' },
          { opacity: 1, transform: 'translate3d(0,0,0) scale(1)' }
        ], { duration: 360, easing: EASE_OUT, fill: 'both' });
        const visual = slide.querySelector('img,video,picture');
        animate(visual, [
          { transform: 'scale(1.018)' },
          { transform: 'scale(1)' }
        ], { duration: 520, easing: EASE_OUT, fill: 'both' });
      });
    });
    slides.forEach((slide) => observer.observe(slide, { attributes: true, attributeFilter: ['hidden'] }));
    heroObservers.set(hero, observer);
  };

  const imageObservers = new WeakMap();
  const bindProductImages = (root = document) => {
    $$('.product-main-gallery-image', root).forEach((image) => {
      if (imageObservers.has(image)) return;
      const observer = new MutationObserver(() => {
        animate(image, [
          { opacity: 0.42, transform: 'scale(.985)' },
          { opacity: 1, transform: 'scale(1)' }
        ], { duration: 250, easing: EASE_OUT, fill: 'both' });
      });
      observer.observe(image, { attributes: true, attributeFilter: ['src'] });
      imageObservers.set(image, observer);
    });
  };

  const detailBound = new WeakSet();
  const detailSelector = '.product-details details,.accordion details,.wk-mobile-menu__directory details,[data-wk-category-group]';
  const detailContent = (details) => Array.from(details.children).filter((child) => child.tagName !== 'SUMMARY');

  const bindDetails = (root = document) => {
    $$(detailSelector, root).forEach((details) => {
      if (detailBound.has(details)) return;
      detailBound.add(details);

      details.addEventListener('toggle', () => {
        if (!details.open || reduced()) return;
        detailContent(details).forEach((content, index) => animate(content, [
          { opacity: 0, transform: 'translate3d(0,-6px,0)' },
          { opacity: 1, transform: 'translate3d(0,0,0)' }
        ], { duration: 240, delay: index * 18, easing: EASE_OUT, fill: 'both' }));
      });

      const summary = details.querySelector(':scope > summary');
      summary?.addEventListener('click', (event) => {
        if (!details.open || reduced() || details.dataset.fnMotionClosing === '1') return;
        event.preventDefault();
        details.dataset.fnMotionClosing = '1';
        const content = detailContent(details);
        const animations = content.map((node) => animate(node, [
          { opacity: 1, transform: 'translate3d(0,0,0)' },
          { opacity: 0, transform: 'translate3d(0,-5px,0)' }
        ], { duration: 145, easing: EASE_STANDARD, fill: 'both' })).filter(Boolean);

        const finish = () => {
          details.open = false;
          delete details.dataset.fnMotionClosing;
          content.forEach((node) => { node.style.opacity = ''; node.style.transform = ''; });
        };
        if (!animations.length) finish();
        else Promise.allSettled(animations.map((animation) => animation.finished)).then(finish);
      }, true);
    });
  };

  const dynamicObservers = new WeakMap();
  const bindDynamicResults = (root = document) => {
    $$('[data-predictive-results],[data-enterprise-search-results]', root).forEach((container) => {
      if (dynamicObservers.has(container)) return;
      const observer = new MutationObserver(() => {
        const results = $$('.predictive-card,a,[role="option"]', container).slice(0, 8);
        results.forEach((item, index) => animate(item, [
          { opacity: 0, transform: 'translate3d(0,7px,0)' },
          { opacity: 1, transform: 'translate3d(0,0,0)' }
        ], { duration: 230, delay: index * 24, easing: EASE_OUT, fill: 'both' }));
        prepareReveal(container);
      });
      observer.observe(container, { childList: true, subtree: true });
      dynamicObservers.set(container, observer);
    });
  };

  const pulseCommerce = () => {
    if (reduced()) return;
    $$('[data-cart-count],[data-header-cart-total],[data-wk73-total],[data-wk73-mobile-total],[data-product-price-current]')
      .forEach((element) => animate(element, [
        { transform: 'scale(1)', opacity: 1 },
        { transform: 'scale(1.055)', opacity: 0.82 },
        { transform: 'scale(1)', opacity: 1 }
      ], { duration: 260, easing: EASE_OUT }));
  };

  ['wokora:cart-updated', 'wokora:cart-v73', 'wokora:vat-updated', 'foodnet:variant-change']
    .forEach((name) => window.addEventListener(name, pulseCommerce));
  document.addEventListener('wokora:cart-updated', pulseCommerce);
  document.addEventListener('wokora:vat-updated', pulseCommerce);

  const bindAll = (root = document) => {
    prepareReveal(root);
    bindClosingTargets(root);
    $$('[data-hero]', root).forEach(bindHero);
    bindProductImages(root);
    bindDetails(root);
    bindDynamicResults(root);
  };

  const dynamicRootObserver = new MutationObserver((mutations) => {
    let needsBind = false;
    for (const mutation of mutations) {
      if (mutation.addedNodes.length) { needsBind = true; break; }
    }
    if (!needsBind) return;
    window.requestAnimationFrame(() => bindAll(document));
  });

  const boot = () => {
    document.documentElement.classList.add('fn-motion-ready');
    bindAll(document);
    if (document.body) dynamicRootObserver.observe(document.body, { childList: true, subtree: true });
  };

  motionQuery?.addEventListener?.('change', () => {
    if (reduced()) {
      document.querySelectorAll('.fn-motion-reveal').forEach((element) => element.classList.add('is-fn-motion-visible'));
    }
  });

  document.addEventListener('shopify:section:load', (event) => bindAll(event.target || document));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
